import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Show, UserButton, useAuth } from "@clerk/react";
import { toast } from "sonner";
import axios, { API, authed, apiError } from "@/lib/api";
import { Brand } from "@/components/site/Header";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import BookingCard from "@/components/admin/BookingCard";
import SettingsPanel from "@/components/admin/SettingsPanel";
import UsersPanel from "@/components/admin/UsersPanel";
import AdminCalendar from "@/components/admin/AdminCalendar";
import { useAdminAction } from "@/lib/adminApi";

const FILTERS = [["active", "À traiter"], ["confirmed", "Confirmées"], ["declined", "Refusées"], ["cancelled", "Annulées"], ["all", "Toutes"]];
const isActive = (s) => ["pending", "approved", "payment_submitted"].includes(s);

const Stat = ({ label, value, testid }) => (
  <div className="bg-[#0A1325] border border-white/10 p-5"><p className="text-xs uppercase tracking-[0.2em] text-slate-500">{label}</p>
    <p className="font-display text-4xl text-[#D4AF37] mt-1" data-testid={testid}>{value}</p></div>
);

const Bookings = ({ getToken, pct }) => {
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("active");
  const load = useCallback(async () => {
    try { setList((await axios.get(`${API}/admin/bookings`, await authed(getToken))).data); }
    catch (e) { toast.error(apiError(e)); }
  }, [getToken]);
  useEffect(() => { load(); }, [load]);
  const call = useAdminAction(getToken, load);
  const act = (id, action, body = {}) => call("post", `/admin/bookings/${id}/${action}`, body);
  const shown = list.filter((b) => filter === "all" || (filter === "active" ? isActive(b.status) : b.status === filter));
  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Stat label="En attente" value={list.filter((b) => b.status === "pending").length} testid="stat-pending" />
        <Stat label="Acompte attendu" value={list.filter((b) => b.status === "approved").length} testid="stat-approved" />
        <Stat label="Paiement déclaré" value={list.filter((b) => b.status === "payment_submitted").length} testid="stat-submitted" />
        <Stat label="Confirmées" value={list.filter((b) => b.status === "confirmed").length} testid="stat-confirmed" />
      </div>
      <div className="flex flex-wrap gap-2 mb-6">
        {FILTERS.map(([k, l]) => <button key={k} onClick={() => setFilter(k)} data-testid={`filter-${k}`}
          className={`px-4 py-2 text-sm border transition-colors duration-300 ${filter === k ? "border-[#D4AF37] text-[#D4AF37]" : "border-white/10 text-slate-400 hover:text-white"}`}>{l}</button>)}
      </div>
      <div className="space-y-4" data-testid="admin-bookings-list">
        {shown.length === 0 && <p className="text-slate-500" data-testid="admin-empty">Aucune demande.</p>}
        {shown.map((b) => <BookingCard key={b.id} b={b} defaultPct={pct} onAction={act} />)}
      </div>
    </>
  );
};

const Dashboard = () => {
  const { getToken } = useAuth();
  const [role, setRole] = useState(null);
  const [pct, setPct] = useState(30);
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/me`, await authed(getToken));
        setRole(r.data.role);
        if (r.data.role === "admin") setPct((await axios.get(`${API}/admin/settings`, await authed(getToken))).data.deposit_pct);
      } catch (e) { toast.error(apiError(e)); setRole("member"); }
    })();
  }, [getToken]);
  if (!role) return <p className="text-slate-400">Chargement…</p>;
  if (role !== "admin") return <p className="text-slate-300" data-testid="admin-denied">Accès réservé au propriétaire de Jolly Roger LTD.</p>;
  return (
    <Tabs defaultValue="bookings">
      <TabsList className="bg-transparent border-b border-white/10 rounded-none w-full justify-start h-auto p-0 mb-8 gap-6 overflow-x-auto">
        {[["bookings", "Demandes"], ["calendar", "Calendrier"], ["settings", "Paiement Juice"], ["users", "Accès"]].map(([v, l]) => (
          <TabsTrigger key={v} value={v} data-testid={`tab-${v}`} className="rounded-none px-0 pb-3 data-[state=active]:bg-transparent data-[state=active]:text-[#D4AF37] data-[state=active]:border-b-2 data-[state=active]:border-[#D4AF37] data-[state=active]:shadow-none">{l}</TabsTrigger>
        ))}
      </TabsList>
      <TabsContent value="bookings"><Bookings getToken={getToken} pct={pct} /></TabsContent>
      <TabsContent value="calendar"><AdminCalendar getToken={getToken} pct={pct} /></TabsContent>
      <TabsContent value="settings"><SettingsPanel getToken={getToken} onSaved={(s) => setPct(s.deposit_pct)} /></TabsContent>
      <TabsContent value="users"><UsersPanel getToken={getToken} /></TabsContent>
    </Tabs>
  );
};

export default function Admin() {
  return (
    <div className="min-h-screen" data-testid="admin-page">
      <header className="border-b border-white/10 bg-[#050B14]/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-5 sm:px-10 h-20 flex items-center justify-between">
          <Brand /><Show when="signed-in"><UserButton /></Show>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-5 sm:px-10 py-10">
        <h1 className="font-display text-4xl sm:text-5xl mb-8">Espace admin</h1>
        <Show when="signed-in"><Dashboard /></Show>
        <Show when="signed-out"><Navigate to="/sign-in" replace /></Show>
      </main>
    </div>
  );
}
